import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog'; // kitlint-allow-material: opened by the shared PayableActionsService, whose callers live outside payables-dashboard
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import moment from 'moment-timezone';
import { Payable } from '../../../../../models/entities/payable';
import { PayableFulfillmentType } from '../../../../../models/entities/payable-fulfillment';

export interface CancelPaymentDialogInput {
    payable: Payable;
}

export interface CancelPaymentDialogResult {
    success: boolean;
    cancellationDate?: string;
}

@Component({
    selector: 'app-cancel-payment-dialog',
    templateUrl: './cancel-payment-dialog.component.html',
    styleUrls: ['./cancel-payment-dialog.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CancelPaymentDialogComponent {
    form: UntypedFormGroup;
    today = moment();
    maxDate = moment();

    /**
     * Cancelling an ACH transfer is a TRUE cancellation, not a void (SD-10651).
     *
     * Every other payment method this dialog serves records a void: PayHOA books a counter journal
     * entry dated to the day the user picks, which is why the date field exists at all. An ACH
     * cancellation books nothing — the backend skips the counter entry for this fulfillment type —
     * so asking for a date would collect a value nothing consumes, and the check-void copy below
     * ("request a stop payment through your bank") describes a situation that is not happening.
     */
    readonly isAchCancellation: boolean;

    /**
     * A marked-as-paid payable was paid outside PayHOA entirely — there is no check to stop, so
     * the check-void copy does not apply. The user has to reverse it with their bank or the
     * vendor themselves (SD-10666).
     */
    readonly isMarkedAsPaid: boolean;

    // Everything but ACH is a void (matching the VOID menu action, SD-10649), so it reads as one.
    readonly title: string;
    readonly confirmButtonText: string;

    constructor(
        private fb: UntypedFormBuilder,
        private _dialogRef: MatDialogRef<CancelPaymentDialogComponent>, // kitlint-allow-material: opened by the shared PayableActionsService, whose callers live outside payables-dashboard
        @Inject(MAT_DIALOG_DATA) public data: CancelPaymentDialogInput, // kitlint-allow-material: opened by the shared PayableActionsService, whose callers live outside payables-dashboard
    ) {
        this._dialogRef.addPanelClass('kit-dialog');
        const fulfillmentType = this.data?.payable?.currentFulfillment?.fulfillmentType;
        this.isAchCancellation = fulfillmentType === PayableFulfillmentType.FULFILLMENT_TYPE_TREASURY_ACH;
        this.isMarkedAsPaid = fulfillmentType === PayableFulfillmentType.FULFILLMENT_TYPE_MARKED_AS_PAID;
        this.title = this.isAchCancellation ? 'Cancel payment?' : 'Void Payment?';
        this.confirmButtonText = this.isAchCancellation ? 'Yes, cancel it' : 'Yes, void it';

        // The control is only built for the methods that actually void. Leaving it out — rather
        // than building it and hiding the input — is what guarantees confirm() cannot send a
        // cancellationDate for an ACH: there is no value to read.
        this.form = this.fb.group(
            this.isAchCancellation ? {} : { cancellationDate: [moment().format('YYYY-MM-DD')] }
        );
    }

    get maxDateStr(): string {
        return this.maxDate.format('YYYY-MM-DD');
    }

    cancel(): void {
        this._dialogRef.close();
    }

    confirm(): void {
        // Annotated locals rather than `close(<CancelPaymentDialogResult>{ ... })`: an
        // angle-bracket assertion on an object literal SUPPRESSES TypeScript's excess-property
        // check, so `cancelationDate` (one 'l') would compile clean — and because
        // `cancellationDate` is optional here and payables.service.ts drops a falsy value from the
        // request body entirely, that typo would post an empty body and let the backend date the
        // void itself, with nothing raised anywhere. An annotated variable is checked structurally.
        if (this.isAchCancellation) {
            const achResult: CancelPaymentDialogResult = { success: true };
            this._dialogRef.close(achResult);
            return;
        }

        const cancellationDate = this.form.get('cancellationDate').value;
        const result: CancelPaymentDialogResult = {
            success: true,
            cancellationDate: cancellationDate ? moment(cancellationDate).format('YYYY-MM-DD') : null
        };
        this._dialogRef.close(result);
    }
}
